import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Check, Upload } from 'lucide-react';
import * as React from 'react';

import {
  Alert, AlertDescription, Button, Card, CardContent, Label, Textarea,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { api, ApiError } from '@/lib/api';

interface RowProblem {
  line: number;
  message: string;
}

interface ImportResult {
  dryRun: boolean;
  accepted: number;
  created: number;
  problems: RowProblem[];
  preview: {
    line: number;
    date: string;
    person: string;
    project: string;
    activity: string;
    duration: string;
  }[];
}

const EXAMPLE = `Date,Person,Project,Activity,Duration,Tags,Note
2026-09-01,asha@innoctive.com,Project CSMC,Production Support,8:00,Development,Fixed the login bug
2026-09-02,asha@innoctive.com,Project CSMC,Testing,7.5,,
2026-09-03,bilal@innoctive.com,Project Wirtgen,Meetings,90m,R&D,Sprint planning`;

/**
 * Bulk import from a spreadsheet.
 *
 * Two things this page insists on, because they are what makes an import usable
 * rather than a thing people give up on:
 *
 *  - It always checks before it writes. The first click is a preview; nothing is
 *    saved until somebody sees what was understood and asks for it again.
 *  - Every rejection carries the line number the spreadsheet shows. "3 rows were
 *    invalid" is useless against a 400-row file.
 */
export function ImportPage() {
  const [csv, setCsv] = React.useState('');
  const [result, setResult] = React.useState<ImportResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.post<ImportResult>('/import/timesheet', { csv, dryRun }),
  });

  const submit = async (dryRun: boolean) => {
    setError(null);
    setResult(null);
    try {
      setResult(await run.mutateAsync(dryRun));
    } catch (err) {
      // The API's message names the missing column or the row limit, which is the
      // part somebody can act on.
      setError(err instanceof ApiError ? err.message : 'Could not read that file.');
    }
  };

  const readFile = async (file: File) => {
    setResult(null);
    setError(null);
    setCsv(await file.text());
  };

  const clean = result !== null && result.problems.length === 0;

  return (
    <PageContainer>
      <PageHeader
        title="Import"
        description="Load recorded time from a spreadsheet. Checked before anything is saved."
      />

      <Card>
        <CardContent className="space-y-4 py-5">
          <div className="space-y-1.5">
            <Label htmlFor="csv-file">Choose a CSV file</Label>
            <input
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void readFile(file);
              }}
              className="block w-full cursor-pointer rounded-md border bg-background p-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm"
            />
            <p className="text-xs leading-relaxed text-muted-foreground">
              Needs <code className="font-mono">Date</code>,{' '}
              <code className="font-mono">Person</code>, <code className="font-mono">Project</code>,{' '}
              <code className="font-mono">Activity</code> and{' '}
              <code className="font-mono">Duration</code> columns.{' '}
              <code className="font-mono">Tags</code> and <code className="font-mono">Note</code>{' '}
              are optional. A file exported from All times imports back unchanged.
            </p>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="csv-text">…or paste it</Label>
              <Button hint={"Fill the box with a sample CSV to see the format"} variant="ghost" size="sm" onClick={() => setCsv(EXAMPLE)}>
                Insert an example
              </Button>
            </div>
            <Textarea
              id="csv-text"
              value={csv}
              onChange={(e) => {
                setCsv(e.target.value);
                setResult(null);
              }}
              rows={8}
              spellCheck={false}
              className="font-mono text-xs"
              placeholder={EXAMPLE}
            />
          </div>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button hint={"Check the file without importing anything"}
              variant="outline"
              disabled={csv.trim() === '' || run.isPending}
              onClick={() => void submit(true)}
            >
              {run.isPending ? 'Checking…' : 'Check the file'}
            </Button>
            {/* Only offered once a check has come back clean. An import button that
                is live before anything has been validated invites the click that
                writes 400 half-understood rows. */}
            <Button hint={"Import the rows — offered once the check comes back clean"}
              disabled={!clean || result.accepted === 0 || run.isPending}
              onClick={() => void submit(false)}
            >
              <Upload className="size-4" />
              {clean ? `Import ${result.accepted} rows` : 'Import'}
            </Button>
            {/* Why Import is off. A disabled button's hint never shows. */}
            <span className="text-xs text-muted-foreground">
              {csv.trim() === ''
                ? 'Paste or upload a CSV, then check it.'
                : !result
                  ? 'Check the file first. Import unlocks once the check comes back clean.'
                  : !clean
                    ? 'Fix the problems below, then check again.'
                    : result.accepted === 0
                      ? 'The check found no rows to import.'
                      : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      {result?.created ? (
        <Alert>
          <AlertDescription className="flex items-center gap-2 text-sm">
            <Check className="size-4" />
            Imported {result.created} {result.created === 1 ? 'entry' : 'entries'}.
          </AlertDescription>
        </Alert>
      ) : null}

      {result && result.problems.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center gap-2 border-b px-4 py-3">
              <AlertTriangle className="size-4 text-destructive" />
              <span className="text-sm font-semibold">
                {result.problems.length}{' '}
                {result.problems.length === 1 ? 'problem' : 'problems'} — nothing was saved
              </span>
            </div>
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Line</TableHead>
                    <TableHead>What is wrong</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.problems.map((problem, i) => (
                    <TableRow key={`${problem.line}-${i}`}>
                      {/* The line number the spreadsheet shows, so it can be found. */}
                      <TableCell className="ts-num font-medium">{problem.line}</TableCell>
                      <TableCell className="text-sm">{problem.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {result && result.problems.length === 0 && result.preview.length > 0 && !result.created && (
        <Card>
          <CardContent className="p-0">
            <div className="border-b px-4 py-3">
              <span className="text-sm font-semibold">
                {result.accepted} {result.accepted === 1 ? 'row' : 'rows'} understood
              </span>
              <span className="ml-2 text-xs text-muted-foreground">
                first {result.preview.length} shown — check the mapping before importing
              </span>
            </div>
            <div className="overflow-x-auto">
              <Table className="min-w-[720px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Line</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Person</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Activity</TableHead>
                    <TableHead className="text-right">Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.preview.map((row) => (
                    <TableRow key={row.line}>
                      <TableCell className="ts-num text-muted-foreground">{row.line}</TableCell>
                      <TableCell className="ts-num">{row.date}</TableCell>
                      <TableCell>{row.person}</TableCell>
                      <TableCell className="text-sm">{row.project}</TableCell>
                      <TableCell className="text-sm">{row.activity}</TableCell>
                      <TableCell className="ts-num text-right">{row.duration}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}
